import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectProgram } from "../../../../utils/gtm";

interface IProps {
    carouselList: ICarousel[];
}

interface ICarousel {
    id: number,
    title: string,
    thumbnailImageUrl: string,
    programPeriod: [string, string],
    bookingPeriod: [string, string],
    locationName: string;
    isDisableBooking: boolean
    isOnline: boolean
}

const WellnessProgramCarousel = ({ carouselList = [] }: IProps) => {
    const [roopList, setRoopList] = useState<ICarousel[]>([...carouselList, ...carouselList, ...carouselList, ...carouselList])
    const [current, setCurrent] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [startX, setStartX] = useState<number>(0);
    const [dragOffset, setDragOffset] = useState<number>(0);
    const [autoSlide, setAutoSlide] = useState<boolean>(true);
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    const startAutoSlide = () => {
        return setInterval(() => {
            if (autoSlide) {
                setCurrent((curr) => (curr === (carouselList.length * 2 - 1) ? carouselList.length : curr + 1));
            }
        }, 5000);
    };

    const onDragStart = (e: React.MouseEvent | React.TouchEvent) => {
        const clientX = e.nativeEvent instanceof MouseEvent ? e.nativeEvent.clientX : (e as React.TouchEvent).touches?.[0].clientX;
        setIsDragging(true);
        setStartX(clientX);
        setAutoSlide(false);
    };

    const onDragMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDragging) return;
        const clientX = e.nativeEvent instanceof MouseEvent ? e.nativeEvent.clientX : (e as React.TouchEvent).touches?.[0].clientX;
        setDragOffset(clientX - startX);
    };

    const onDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDragging) return;
        setIsDragging(false);
        const threshold = 100;
        if (dragOffset > threshold) {
            setCurrent((prev) => (prev === 0 ? 0 : prev - 1));
        } else if (dragOffset < -threshold) {
            setCurrent((prev) => (prev === (carouselList.length * 2 - 1) ? carouselList.length : prev + 1));
        }
        setDragOffset(0);
        setAutoSlide(true);
    };

    const CheckBookingAvailableStatus = ({ programPeriod, bookingPeriod, isDisableBooking }: { programPeriod: [string, string], bookingPeriod: [string, string], isDisableBooking: boolean }) => {
        const [startProgram, endProgram] = programPeriod.map(q => dayjs(q))
        const [startBooking, endBooking] = bookingPeriod.map(q => dayjs(q))

        if (dayjs().isAfter(endProgram.endOf('d'))) {
            return <div className="caption/c1" style={{ position: 'absolute', top: '12px', left: '12px', backgroundColor: 'var(--dimd200)', padding: '4px', borderRadius: '4px', color: 'var(--secondarys100)' }}>종료</div>;
        } else if (dayjs().isBefore(startBooking)) {
            return <div className="caption/c1" style={{ position: 'absolute', top: '12px', left: '12px', backgroundColor: 'var(--dimd200)', padding: '4px', borderRadius: '4px', color: 'var(--secondarys100)' }}>오픈 예정</div>;
        } else if (dayjs().isAfter(endBooking) || isDisableBooking) {
            return <div className="caption/c1" style={{ position: 'absolute', top: '12px', left: '12px', backgroundColor: 'var(--dimd200)', padding: '4px', borderRadius: '4px', color: 'var(--secondarys100)' }}>예약 마감</div>;
        } else {
            return <></>;
        }
    }

    const getProgramState = (programPeriod: [string, string], bookingPeriod: [string, string], isDisableBooking: boolean): "오픈예정" | "예약가능" | "예약마감" | "종료" => {
        const [startProgram, endProgram] = programPeriod.map(dayjs);
        const [startBooking, endBooking] = bookingPeriod.map(dayjs);
        const now = dayjs();

        if (now.isAfter(endProgram.endOf("day"))) {
            return "종료";
        }
        if (now.isBefore(startBooking)) {
            return "오픈예정";
        }
        if (now.isAfter(endBooking) || isDisableBooking) {
            return "예약마감";
        }
        return "예약가능";
    };

    const onClickProgram = (id: number, title: string, programPeriod: [string, string], bookingPeriod: [string, string], isDisableBooking: boolean) => {
        const state = getProgramState(programPeriod, bookingPeriod, isDisableBooking);

        selectProgram(title, state)

        navigate(`/wellness-program/${id}`);
    }

    useEffect(() => {
        const interval = startAutoSlide();
        return () => clearInterval(interval);
    }, [carouselList.length, autoSlide]);

    useEffect(() => {
        if (!cardRef.current || !containerRef.current || (carouselList.length === 1)) return;
        const targetOffsetWidth = cardRef.current?.getBoundingClientRect().width;;
        const returnValue = (carouselList.length + current) * targetOffsetWidth;
        const offsetPx = -(returnValue - dragOffset);
        const value = `translateX(calc(${offsetPx}px + 3.063vw - ${current === 0 ? 2.134 : 0}vw))`;
        containerRef.current.style.transform = value;
    }, [current, dragOffset])

    return <>
        {carouselList.length === 1
            ? carouselList.map((carousel, idx) =>
                <div key={idx} style={{ width: '85.34vw', maxWidth: '409.632px', marginInline: 'auto' }} ref={idx === 0 ? cardRef : null} >
                    <div style={{ position: 'relative', borderRadius: '4px', paddingTop: '56.25%', backgroundImage: `url(${carousel.thumbnailImageUrl})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }} onClick={() => navigate(`/wellness-program/${carousel.id}`)}>
                        <CheckBookingAvailableStatus programPeriod={carousel.programPeriod} bookingPeriod={carousel.bookingPeriod} isDisableBooking={carousel.isDisableBooking} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '4px', paddingTop: '12px' }}>
                        <div className="sub-title/st3" style={{ color: 'var(--baseblack)' }}>
                            {carousel.title}
                        </div>
                        <div className="body/b1" style={{ color: 'var(--secondarys500)' }}>
                            {dayjs(carousel.programPeriod[0]).format('YYYY.MM.DD')} - {dayjs(carousel.programPeriod[1]).format('YYYY.MM.DD')}{carousel.isOnline ? '' : ' | ' + carousel.locationName}
                        </div>
                    </div>
                </div>)
            : <div style={{ display: "flex", overflowX: 'hidden' }}
                onMouseDown={onDragStart}
                onTouchStart={onDragStart}
                onMouseMove={onDragMove}
                onTouchMove={onDragMove}
                onMouseUp={onDragEnd}
                onTouchEnd={onDragEnd}
                onMouseLeave={onDragEnd}
            >
                <div
                    ref={containerRef}
                    style={{ display: "flex", transition: current === 0 || current === carouselList.length || isDragging ? "transform 0.001s ease" : "transform 0.5s ease" }}>
                    {roopList.map((carousel, idx) =>
                        <div key={idx} style={{ paddingLeft: '4.267vw', width: '85.34vw', maxWidth: 'calc(480px - 14.66vw)', visibility: current === 0 && idx === carouselList.length - 1 ? 'hidden' : 'visible' }} ref={idx === 0 ? cardRef : null} >
                            <div style={{ position: 'relative', borderRadius: '4px', paddingTop: '56.25%', backgroundImage: `url(${carousel.thumbnailImageUrl})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }} onClick={() => onClickProgram(carousel.id, carousel.title, carousel.programPeriod, carousel.bookingPeriod, carousel.isDisableBooking)}>
                                <CheckBookingAvailableStatus programPeriod={carousel.programPeriod} bookingPeriod={carousel.bookingPeriod} isDisableBooking={carousel.isDisableBooking} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '4px', paddingTop: '12px' }}>
                                <div className="sub-title/st3" style={{ color: 'var(--baseblack)' }}>
                                    {carousel.title}
                                </div>
                                <div className="body/b1" style={{ color: 'var(--secondarys500)' }}>
                                    {dayjs(carousel.programPeriod[0]).format('YYYY.MM.DD')} - {dayjs(carousel.programPeriod[1]).format('YYYY.MM.DD')}{carousel.isOnline ? '' : ' | ' + carousel.locationName}
                                </div>
                            </div>
                        </div>)}
                </div>
            </div>}
    </>
}
export { WellnessProgramCarousel }